import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminSidebar } from '../../components/Sidebar';
import api from '../../utils/api';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const dur = (start, end) => {
  if (!start || !end) return '—';
  const mins = Math.round((new Date(end) - new Date(start)) / 60000);
  return mins < 60 ? `${mins} Min.` : `${Math.floor(mins / 60)}h ${mins % 60}min`;
};

export default function QuizSessions() {
  const { id } = useParams();
  const [quizTitle, setQuizTitle] = useState('');
  const [sessions, setSessions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [loadingP, setLoadingP] = useState(false);

  useEffect(() => {
    api.get(`/admin/quizzes/${id}`).then(r => setQuizTitle(r.data.quiz?.title || 'Quiz')).catch(() => {});
    api.get(`/admin/quizzes/${id}/sessions`).then(r => setSessions(r.data.sessions));
  }, [id]);

  const loadParticipants = async (sessionId) => {
    if (selected === sessionId) { setSelected(null); setParticipants(null); return; }
    setSelected(sessionId);
    setLoadingP(true);
    const r = await api.get(`/admin/sessions/${sessionId}/participants`);
    setParticipants(r.data.participants);
    setLoadingP(false);
  };

  const downloadResultsPDF = async (sessionId) => {
    try {
      const res = await fetch(`/api/admin/sessions/${sessionId}/results-pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('zq_token')}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Fehler');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ergebnisse-${sessionId}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { alert('PDF konnte nicht geladen werden.'); }
  };

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Link to={`/admin/quizzes/${id}`} style={{ color: 'var(--text3)', fontSize: 13, textDecoration: 'none' }}>
            Zuruck zum Quiz
          </Link>
          <span style={{ color: 'var(--border)' }}>/</span>
          <h1 style={{ fontSize: 24, margin: 0 }}>{quizTitle} — Ergebnisse</h1>
        </div>

        {!sessions ? (
          <div className="spinner-wrap"><div className="spinner" /></div>
        ) : sessions.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
            Noch keine Spielrunden fur dieses Quiz.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sessions.map((s) => (
              <div key={s.id} className="card" style={{ padding: 0 }}>
                <div
                  style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
                  onClick={() => loadParticipants(s.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                      {fmt(s.started_at)}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      Dauer: {dur(s.started_at, s.finished_at)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 60 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)' }}>{s.participant_count}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Teilnehmer</div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 60 }}>
                    <div style={{ fontSize: 22, fontWeight: 800 }}>{s.top_score ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Top-Punkte</div>
                  </div>
                  <div>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 100,
                      background: s.status === 'finished' ? '#ECFDF5' : '#E8F0FE',
                      color: s.status === 'finished' ? '#059669' : '#1A56DB',
                    }}>
                      {s.status === 'finished' ? 'Abgeschlossen' : s.status}
                    </span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadResultsPDF(s.id); }}
                    style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text2)', whiteSpace: 'nowrap' }}
                    title="Ergebnisse als PDF"
                  >
                    PDF
                  </button>
                  <div style={{ color: 'var(--text3)', fontSize: 18, transform: selected === s.id ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
                    &#8964;
                  </div>
                </div>

                {selected === s.id && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '0 20px 20px' }}>
                    {loadingP ? (
                      <div style={{ padding: 24, textAlign: 'center' }}><div className="spinner" /></div>
                    ) : !participants?.length ? (
                      <div style={{ padding: 20, color: 'var(--text3)', fontSize: 13 }}>Keine Teilnehmer vorhanden.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            {['#', 'Name', 'Richtig', 'Punkte', 'Gruppe', 'Sprache'].map(h => (
                              <th key={h} style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textAlign: h === '#' || h === 'Punkte' ? 'center' : 'left', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {participants.map((p) => (
                            <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: p.rank <= 3 ? 'var(--primary)' : 'var(--text3)', fontSize: 13 }}>
                                {p.rank === 1 ? '1' : p.rank === 2 ? '2' : p.rank === 3 ? '3' : p.rank}
                              </td>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.first_name} {p.last_name}</td>
                              <td style={{ padding: '10px 12px', color: 'var(--text2)', fontSize: 13 }}>
                                {p.correct_answers ?? 0} / {p.total_questions ?? '—'}
                              </td>
                              <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 800, fontSize: 16, color: 'var(--primary)' }}>{p.total_score ?? 0}</td>
                              <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>{p.age_group_label || '—'}</td>
                              <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>{p.language === 'al' ? 'Albanisch' : 'Deutsch'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
